import { SQUAD_COLORS } from './templateConstants';

/**
 * Font sizes are measured from SQUAD's own PowerPoint exports, not derived.
 *
 * Two originals agree to the third decimal — FS_SQUAD_Value_A and
 * FS_SQUAD_Aguja_Bond_Opp_R both place their logo at 114.225 × 47.983 pt and set
 * body copy 8, section bars 11, small print 6. The page title is autofit by
 * PowerPoint: 29 for the short "SQUAD Value - A", 25 for the longer
 * "SQUAD Aguja Bond Opportunities - R".
 *
 * An earlier export of the Aguja factsheet measured exactly 1.08× these values and
 * was mistaken for a slide-to-A4 scaling factor. It was a differently scaled export
 * of the same document (JPEG assets, content drawn in a larger coordinate space),
 * not a property of the design.
 */

export const SQUAD_PAGE_HEADER = {
  logoSquadFontSize: 24,
  logoFondsFontSize: 14,
  logoBlockWidthFallback: 114,
  logoBlockWidthPdf: 114,
  /**
   * Official SQUAD FONDS mark (PNG ~2.63:1).
   * Sized against the original factsheet, which places its logo at 114.2 × 48.0 pt;
   * matching its width keeps our aspect ratio intact.
   */
  logoImageWidth: 114,
  logoImageHeight: 43,
  logoImageWidthPdf: 114,
  logoImageHeightPdf: 43,
  /** Original: 29 pt for a short fund name; longer ones are shrunk by the autofit. */
  titleFontSize: 29,
  titleLetterSpacing: 0.1,
  /** Matches the fund title on squad-fonds.de. */
  titleLineHeight: 1.2,
  /** Preview base before scale — browser min-font clamps raw px; scale applied in sharedStyles. */
  metaFontSize: 10,
  /** Visual scale for Werbemitteilung / page counter (0.45 was too small; 0.9 ≈ 9 px). */
  metaFontScale: 0.9,
  /** PDF (pt) — same base as the body copy. */
  metaFontSizePdf: 8,
  metaColor: SQUAD_COLORS.navy,
  titleRowMetaGap: 4,
  headerPaddingBottom: 0,
  /** Gap under meta row (“Werbemitteilung” / page) before first navy bar. */
  headerMarginBottom: 8,
  bodyRowPaddingTop: 0,
  sidebarPaddingTop: 11,
  logoMarginBottom: 6,
  /** Must stay > 0 — logo image is shorter than the old text mark; 0 collapses onto Anlagestrategie. */
  pdfHeaderMarginBottom: 8,
  pdfLogoMarginBottom: 0,
  pdfSidebarPaddingTop: 5,
  pdfPagePaddingTop: 16,
  pdfPagePaddingBottom: 14,
  pdfPagePaddingHorizontal: 16,
  pdfSidebarPaddingHorizontal: 7,
  pdfSidebarPaddingBottom: 7,
  headerFontFamily: 'SegoeUI, "Segoe UI", system-ui, -apple-system, sans-serif',
} as const;

/**
 * PDF header row height (pt) on pages 1–3: the logo or the title + meta block, whichever
 * is taller (meta lineHeight buffer so descenders are not clipped). Depends on the title
 * size actually used — long fund names shrink the title (see squadTitleFit), otherwise
 * the first section bar covers the title line.
 */
export const squadPdfHeaderRowHeight = (titleFontSize: number = SQUAD_PAGE_HEADER.titleFontSize) =>
  Math.max(
    SQUAD_PAGE_HEADER.logoImageHeightPdf,
    Math.ceil(titleFontSize * SQUAD_PAGE_HEADER.titleLineHeight) +
      SQUAD_PAGE_HEADER.titleRowMetaGap +
      Math.ceil(SQUAD_PAGE_HEADER.metaFontSizePdf * 1.2),
  );

/** Top of the PDF main column's content box on pages 1–3 (pt), below the fixed header. */
export const squadPdfBodyTop = (titleFontSize: number = SQUAD_PAGE_HEADER.titleFontSize) =>
  SQUAD_PAGE_HEADER.pdfPagePaddingTop +
  squadPdfHeaderRowHeight(titleFontSize) +
  SQUAD_PAGE_HEADER.pdfHeaderMarginBottom;

export const SQUAD_BODY_LAYOUT = {
  sidebarWidth: '32%',
  sidebarMaxWidth: 180,
  columnGap: 12,
} as const;

/** Body/content typography — web preview (px). Min size 8px. */
export const SQUAD_TYPOGRAPHY = {
  /** Sidebar section headers (~1.25× PDF goldBar 8 pt). */
  goldBar: 10,
  /** 1.25x the body, as in the PDF (10 pt against an 8 pt base). */
  navyBar: 13,
  /** Sidebar fondsdaten rows (~1.25× PDF kv 8 pt). */
  kv: 10,
  body: 11,
  footnote: 11,
  /** Footnotes — the PDF's 6 pt `note` at the preview scale (794 px per 595.28 pt). */
  note: 8,
  sidebarSection: 15,
  bar: 11,
  small: 8,
  /** Main-column chart/table footnotes (~1.5× prior 6 px caption). */
  caption: 9,
  tiny: 8,
  placeholder: 10,
  advisorName: 10,
  advisorCompany: 10,
  advisorLogoLine1: 10,
  advisorLogoLine2: 9,
  advisorBio: 10,
  kontaktCompany: 11,
  kontakt: 11,
  table: 9,
  riskBox: 10,
  /** Risk-indicator body — mirrors PDF `riskText` 8 pt (~1.375× → 11 px). */
  riskText: 11,
  /**
   * Risk scale labels ("Potentiell … Ertrag") — the PDF uses 6 pt.
   * Keep preview under the content base so the 180px sidebar does not overlap.
   */
  riskScale: 6,
} as const;

/**
 * MiFID overlay notice — web preview only.
 * Browsers often clamp sub-8px fonts; scale down from baseFontSizePx instead (PDF uses SQUAD_TYPOGRAPHY_PDF).
 */
export const SQUAD_MIFID_PREVIEW_NOTICE = {
  baseFontSizePx: 8,
  /** Effective ≈ 5.2px — matches the 6 pt notice in the chart/table overlay boxes. */
  scale: 0.65,
} as const;

/** Body/content typography — react-pdf (pt), measured from the originals. */
export const SQUAD_TYPOGRAPHY_PDF = {
  /** Content text. */
  base: 8,
  kv: 8,
  body: 8,
  /** Disclaimer text on page 4 — not the footnotes, see `note`. */
  footnote: 8,
  /**
   * Footnotes: sidebar notes, sources under charts and tables, the notes at the page
   * foot. Same size as the risk scale labels, in navy (SQUAD 30.10.2026, approved by PEH).
   */
  note: 6,
  bar: 8,
  portfolioBar: 8,
  tableCell: 8,
  small: 8,
  tiny: 8,
  placeholder: 8,
  advisorName: 8,
  advisorCompany: 8,
  advisorBio: 8,
  kontaktCompany: 8,
  kontakt: 8,
  riskBox: 8,
  bullet: 8,
  riskText: 8,
  /**
   * Risk indicator scale labels — 6 pt, as in the originals.
   * "Potentiell geringerer Ertrag" / "Potentiell höherer Ertrag" sit side by side in
   * the 180 pt sidebar; on the content base of 8 they overlap.
   */
  riskScale: 6,
  /**
   * Section bars.
   * goldBar heads the sidebar tables and sits on the table's own size, as SQUAD asked
   * ("Schriftgröße aller Header im goldenen Bereich auf gleiche Größe wie Tabelle").
   * navyBar is what makes the 5 mm bar possible — see SQUAD_SECTION_GEOMETRY_PDF.
   */
  goldBar: 8,
  navyBar: 10,
  sidebarSection: 11,
  /** Advisor logo lettering — stands in for an image logo, deliberately off the content base. */
  advisorLogoLine1: 13,
  advisorLogoLine2: 12,
  /** Small print — 6 pt, like the source notes in the originals. */
  mifidOverlayNotice: 6,
} as const;

/**
 * Chart axis labels are set in CSS px inside the chart, which is then rasterised and
 * placed on the PDF page — so their size on paper depends on the ratio between the
 * preview column and the PDF column, not on the capture scale.
 *
 * Preview main column: 794 px page − 2 × 1 px border − 2 × 22 px padding − sidebar −
 *                      column gap.
 * PDF main column:     595.28 pt A4 − 2 × page padding − sidebar − column gap.
 *
 * The preview page is border-box with a 1 px border, and the preview sidebar takes its
 * 7 px padding on top of the 180 px max width (the PDF rail is 180 pt including it), so
 * the preview column is 542 px, not 558 — measured in the editor. Sized against 558, the
 * NAV capture landed squashed to 97 % of its height in the PDF and the rolling capture
 * stretched to 103 %, so the same label size came out at two different heights.
 *
 * squadTitleFit still sizes the header title with 558 px. That row sits beside the logo,
 * not the padded sidebar, so its width is a separate question (the 1 px border applies
 * there too); it was not re-measured with this change.
 */
export const SQUAD_PREVIEW_MAIN_WIDTH_PX =
  794 -
  1 * 2 -
  22 * 2 -
  (SQUAD_BODY_LAYOUT.sidebarMaxWidth + SQUAD_PAGE_HEADER.pdfSidebarPaddingHorizontal * 2) -
  SQUAD_BODY_LAYOUT.columnGap;
export const SQUAD_PDF_MAIN_WIDTH_PT =
  595.28 -
  SQUAD_PAGE_HEADER.pdfPagePaddingHorizontal * 2 -
  SQUAD_BODY_LAYOUT.sidebarMaxWidth -
  SQUAD_BODY_LAYOUT.columnGap;

/**
 * Chart axis labels in preview px — both raster charts (NAV, rolling bars).
 *
 * 6 pt in the PDF, the size of the footnotes (SQUAD 30.10.2026). 6 pt is 8.76 px here;
 * rounded to 9 px it lands at 6.17 pt, which is what the rolling chart's y-axis already
 * had — the size SQUAD named as the floor ("nicht kleiner … wie es jetzt ist bei der
 * Rollierenden"). Rounding down would have gone below it.
 */
export const SQUAD_CHART_AXIS_LABEL_PX = Math.round(
  (SQUAD_TYPOGRAPHY_PDF.note * SQUAD_PREVIEW_MAIN_WIDTH_PX) / SQUAD_PDF_MAIN_WIDTH_PT,
);

/**
 * Section header and sidebar table geometry.
 *
 * Measured from SQUAD's layout document of 11.09.2026 ("FS Anpasungen-11.09.2026.pdf").
 * That document is A4-true — its sidebar measures 180.7 pt against our 180 pt — but it
 * sets type 1.08x above our base (8.64 pt body, 10.80 pt bars). Every value below is
 * therefore its measured value scaled by 8 / 8.64, which lands each one on a round
 * number and reproduces the 5 mm bar SQUAD asked for. Do not read the raw 8.64 pt
 * numbers as a size correction: two genuine SQUAD exports set body copy at 8 pt.
 *
 * Navy bar: 15.15 pt measured -> 14.0 pt, i.e. 4.9 mm on paper (7.9 mm before).
 * A 10 pt line box is 13.3 pt (Segoe UI hhea 2724/2048), so 0.8 pt of padding is left.
 *
 * How that padding splits decides where the text sits, because react-pdf puts the
 * baseline at paddingTop + ascent with no half-leading: the line box already hangs
 * 2.5 pt of descent below the baseline. Split evenly (0.4/0.4) the caps sat 0.64 pt
 * low and g/p/y cleared the bar by 0.56 pt — SQUAD saw it on 25.09.2026. All of it
 * below (0/0.8) gives 0.96 pt clearance and caps 0.24 pt low, which is what the
 * source document does (baseline ≈10.8 pt into its bar, scaled). Bar height unchanged.
 *
 * Gold bar: react-pdf offsets text by paddingTop only and ignores borderTopWidth,
 * while Yoga counts the border in the box. With 2.25/2.25 the caps sat 0.99 pt high
 * ("nach oben ausgerichtet"). Moving the 1.5 pt border's worth into paddingTop
 * (3.25/1.25) centres the cap band to 0.01 pt; the box stays 18.14 pt. That puts the
 * baseline 10.38 pt into the 15.14 pt inner box — the layout document has 10.39 of
 * 15.56. Should react-pdf start honouring the border, this drops the text by 1.5 pt —
 * remeasure.
 *
 * Sidebar table: row pitch 17.07 pt measured -> 15.8 pt (13.1 pt before).
 */
export const SQUAD_SECTION_GEOMETRY_PDF = {
  /** Split, not symmetric — see above. Top + bottom is the 0.8 pt the 14 pt bar leaves. */
  navyBarPaddingTop: 0,
  navyBarPaddingBottom: 0.8,
  /** Air above each main-column bar; below it comes from navyBar's own marginBottom. */
  navyBarMarginTop: 3,
  /** Split to make up for the border react-pdf leaves out of the text offset. */
  goldBarPaddingTop: 3.25,
  goldBarPaddingBottom: 1.25,
  /** Air before and after every sidebar section header. */
  goldBarMarginTop: 5,
  goldBarMarginBottom: 4,
  kvRowPaddingVertical: 2.35,
  /** Anlagestrategie / Monatskommentar — "Zeilenabstand der Texte sind zu eng". */
  proseLineHeight: 1.2,
  /**
   * Page 1 is set tighter than pages 2 and 3 (SQUAD 30.10.2026): the air above and below
   * the Anlagestrategie was "ein Ticken" too much, the Monatskommentar should match it,
   * and the gap under the chart source could lose 20–30 %.
   *
   * Space between two page-1 sections, i.e. the main column's gap. Each bar adds
   * navyBarMarginTop, so content -> next bar goes from 5 + 3 = 8 to 3 + 3 = 6 pt (-25 %).
   * Pages 2 and 3 keep 5.
   */
  page1SectionGap: 3,
  /** Bar -> first line of Anlagestrategie and Monatskommentar; every other bar keeps 5. */
  proseBarMarginBottom: 3,
} as const;

/**
 * Preview mirror of SQUAD_SECTION_GEOMETRY_PDF, in px.
 * The preview page is 794 px wide where the PDF is 595.28 pt, so a pt is ~1.33 px —
 * but preview type is set on its own rounded scale, so these are matched by eye
 * against the PDF rather than converted.
 */
export const SQUAD_SECTION_GEOMETRY_PREVIEW = {
  navyBarPaddingVertical: 1,
  navyBarMarginTop: 4,
  goldBarPaddingVertical: 3,
  goldBarMarginTop: 7,
  goldBarMarginBottom: 5,
  kvRowPaddingVertical: 3,
  proseLineHeight: 1.2,
  /**
   * Mirror of page1SectionGap. Page-1 sections are 6 (gap) + 18 (section mb) + 4 (bar mt)
   * = 28 px apart; 11 instead of 18 takes the same quarter off (21 px). The bar -> prose
   * gap needs no mirror: the preview bar already sits 0.9 px above its text.
   */
  page1SectionMarginBottom: 11,
} as const;

export function splitSquadTitle(title: string) {
  const match = String(title || '').match(/^(SQUAD)\s*(.*)$/i);
  return match
    ? { squad: match[1], rest: match[2] }
    : { squad: '', rest: title };
}
